import { GuardarPdf } from "../componentes/GuardarPdf.tsx";
import { useEffect, useState, type ReactNode } from "react";
import { api, puede, type Paciente } from "../api.ts";
import { TIPOS_INDICACION, GUIAS, nombreIndicacion, problemasIndicacion, textoIndicacion, MAX_ADICIONALES, fechaCertificado, type TipoIndicacion } from "../../../dominio/src/index.ts";
import { AvisoError, hoy, Seccion, useAccion } from "./comun.tsx";
import { EnviarWhatsApp } from "../componentes/EnviarWhatsApp.tsx";
import { FechaInput } from "../componentes/CampoFecha.tsx";

export interface IndicacionApi { firmado_digital?: boolean; firma_digital?: "firmado" | "sin_firma" | "vencida" | "error"; id: string; numero: number; tipo: TipoIndicacion; fecha: string; procedimiento: string; adicionales: string; texto: string; firmada_en: string; firmada_por: string; registro: string }

/** Dibuja el texto congelado: apartados con título, viñetas y párrafos. Sirve para la vista previa y para imprimir. */
function CuerpoIndicacion({ texto }: { texto: string }) {
  const [, , guia, datos, ...resto] = texto.split("\n\n");
  const [nombre, ...sub] = guia.split("\n");
  return (
    <div className="indicacion">
      <h3 style={{ textAlign: "center", margin: "4px 0 0" }}>{nombre}</h3>
      <p className="muted" style={{ textAlign: "center", margin: "0 0 8px" }}>{sub.join(" ")}</p>
      <table className="tabla-iess"><tbody>{datos.split("\n").map((l) => { const i = l.indexOf(": "); return <tr key={l}><th>{l.slice(0, i)}:</th><td>{l.slice(i + 2)}</td></tr>; })}</tbody></table>
      {resto.map((b, i) => {
        const l = b.split("\n");
        if (!l[0].startsWith("## ")) return <p key={i} style={{ margin: "8px 0" }}>{b}</p>;
        return (
          <section key={i} style={{ marginTop: 10 }}>
            <h4 style={{ margin: "0 0 2px" }}>{l[0].slice(3)}</h4>
            {l.slice(1).map((x) => <p key={x} style={{ margin: "2px 0", paddingLeft: x.startsWith("• ") ? 10 : 0 }}>{x}</p>)}
          </section>
        );
      })}
    </div>
  );
}

export function Indicaciones({ paciente, alImprimir }: { paciente: Paciente; alImprimir: (i: IndicacionApi) => void }) {
  const [lista, setLista] = useState<IndicacionApi[]>([]);
  const [f, setF] = useState({ tipo: "cirugia_bucal" as TipoIndicacion, fecha: hoy(), procedimiento: "", adicionales: "" });
  const a = useAccion();
  const firma = puede("clinico:firmar");
  const set = (x: Partial<typeof f>) => setF((v) => ({ ...v, ...x }));
  useEffect(() => { api<IndicacionApi[]>("GET", `/api/pacientes/${paciente.id}/indicaciones`).then(setLista).catch(a.setError); }, [paciente.id]);

  const problemas = problemasIndicacion(f, hoy());
  const vista = textoIndicacion({ ...f, ciudad: "", contacto: "", paciente: { nombre: `${paciente.nombres} ${paciente.apellidos}`.trim(), doc: paciente.doc, hc: paciente.hc } });
  async function emitir() {
    const r = await a.correr(() => api<IndicacionApi>("POST", `/api/pacientes/${paciente.id}/indicaciones`, f));
    if (r) { setLista([r, ...lista]); alImprimir(r); }
  }

  return (
    <Seccion titulo="Indicaciones post operatorias">
      {firma && (
        <>
          <div className="seg" role="group" aria-label="Tipo de indicaciones">
            {TIPOS_INDICACION.map((t) => <button key={t} aria-pressed={f.tipo === t} onClick={() => set({ tipo: t })}>{nombreIndicacion(t)}</button>)}
          </div>
          <div className="rejilla" style={{ gridTemplateColumns: "1fr 2fr" }}>
            <label className="campo">{GUIAS[f.tipo].etiquetaFecha}<FechaInput max={hoy()} value={f.fecha} onChange={(x) => set({ fecha: x.target.value })} /></label>
            <label className="campo">Procedimiento realizado (opcional)<input value={f.procedimiento} maxLength={300} onChange={(x) => set({ procedimiento: x.target.value })} placeholder="Ej.: extracción de la pieza 38 · implante en la pieza 46" /></label>
          </div>
          <label className="campo">Indicaciones adicionales (opcional)<textarea rows={2} value={f.adicionales} maxLength={MAX_ADICIONALES} onChange={(x) => set({ adicionales: x.target.value })} placeholder="Algo particular para este paciente" /></label>
          <div className="texto-legal" aria-label="Vista previa de las indicaciones"><CuerpoIndicacion texto={vista} /></div>
          {problemas.length > 0 && <div className="aviso info"><ul>{problemas.map((p) => <li key={p}>{p}</li>)}</ul></div>}
          <AvisoError error={a.error} />
          <button className="btn pri" disabled={a.ocupado || problemas.length > 0} onClick={emitir}>Firmar y emitir indicaciones</button>
        </>
      )}
      <h3>Indicaciones emitidas</h3>
      {lista.length === 0 && <p className="muted" style={{ margin: 0 }}>Aún no hay indicaciones post operatorias.</p>}
      <ul className="lista-abonos">
        {lista.map((i) => (
          <li key={i.id}>
            <div className="fila" style={{ justifyContent: "space-between" }}>
              <span><span className="mono muted">N.º {String(i.numero).padStart(5, "0")}</span> · {nombreIndicacion(i.tipo)} · {fechaCertificado(i.fecha)} · {i.firmada_por}</span>
              {i.firmado_digital && <span className="chip azul" title="PDF firmado digitalmente">Firmado digitalmente</span>}
              <button className="btn chico" onClick={() => alImprimir(i)}>{i.firmado_digital ? "Abrir PDF" : "Imprimir"}</button>
              <GuardarPdf tipo="indicacion" id={i.id} nombre={`Indicaciones-${String(i.numero).padStart(5, "0")}`} />
              <EnviarWhatsApp tipo="indicacion" id={i.id} />
            </div>
            {i.procedimiento && <span style={{ fontSize: 14 }}>{i.procedimiento}</span>}
          </li>
        ))}
      </ul>
    </Seccion>
  );
}

export function IndicacionImpresa({ i, membrete }: { i: IndicacionApi; membrete: ReactNode }) {
  const [lugarFecha, titulo] = i.texto.split("\n\n");
  const cierre = i.texto.split("\n\n").at(-1) ?? "";
  return (
    <div className="solo-impresion certificado">
      {membrete}
      <p style={{ textAlign: "right" }}>{lugarFecha}</p>
      <h2 style={{ textAlign: "center", margin: "12px 0" }}>{titulo}</h2>
      <CuerpoIndicacion texto={i.texto.split("\n\n").slice(0, -1).join("\n\n")} />
      <p>{cierre}</p>
      <p style={{ marginTop: 56, textAlign: "center" }}>______________________________<br />{i.firmada_por}{i.registro && <><br />{i.registro}</>}<br />Odontólogo(a)</p>
      <p style={{ fontSize: 11 }}>Indicaciones N.º {String(i.numero).padStart(5, "0")}</p>
    </div>
  );
}
